import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert, Box, Chip,
  ToggleButton, ToggleButtonGroup, Typography, List, ListItemButton, IconButton, CircularProgress,
} from '@mui/material';
import { ArrowBack as BackIcon, Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useWhatsAppConnection } from '../../contexts/WhatsAppConnectionContext';
import { feedbackService } from '../../services/feedbackService';
import { collectDiagnostics } from '../../utils/diagnostics';
import FeedbackMessages from './FeedbackMessages';

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const lastBody = (t) => (t.messages?.length ? t.messages[t.messages.length - 1].body : '');

// Strip image/file bytes and pathologically long strings, keeping the FLOW STRUCTURE intact
// (a flow with base64 images is otherwise huge, but the bytes aren't useful for debugging).
const trimValue = (s) => {
  const isData = s.startsWith('data:');
  if ((isData && s.length > 256) || s.length > 10000) {
    return `[${isData ? 'data URI' : 'string'} omitted: ${s.length} chars]`;
  }
  return s;
};
const trimDeep = (val, seen) => {
  if (typeof val === 'string') return trimValue(val);
  if (val && typeof val === 'object') {
    if (seen.has(val)) return '[circular]';
    seen.add(val);
    if (Array.isArray(val)) return val.map((v) => trimDeep(v, seen));
    return Object.fromEntries(Object.keys(val).map((k) => [k, trimDeep(val[k], seen)]));
  }
  return val;
};

// Collect page diagnostics, trimming image bytes so the flow structure survives even with
// embedded images. The size guard is now a far backstop, rarely hit after trimming.
const safeDiagnostics = () => {
  try {
    const diag = trimDeep(collectDiagnostics(), new WeakSet());
    const json = JSON.stringify(diag);
    if (json.length > 500000) {
      return { note: 'page state omitted (too large even after trimming)', keys: Object.keys(diag) };
    }
    return diag;
  } catch {
    return { error: 'page state not serialisable' };
  }
};

// Snapshot persisted app state for the report, redacting anything secret (e.g. the WABA
// token stored under whatsapp_token_data) and capping long values.
const SENSITIVE_KEY = /token|secret|password|auth|credential|key/i;
const snapshotStorage = (store) => {
  const out = {};
  try {
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (k) {
        const v = store.getItem(k) ?? '';
        out[k] = SENSITIVE_KEY.test(k) ? '[redacted]' : (v.length > 500 ? `${v.slice(0, 500)}…` : v);
      }
    }
  } catch {
    /* storage access can throw (private mode, etc.) */
  }
  return out;
};

/**
 * A user's feedback inbox: see your threads + the team's replies, reply back, or open a new one.
 * Captures the current screen/context on new threads and asks demo users for an email.
 */
const FeedbackDialog = ({ open, onClose }) => {
  const { user, isDemoMode, authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const { selectedOrganization } = useOrganization();
  const { isConnected } = useWhatsAppConnection();

  const [view, setView] = useState('list'); // 'list' | 'compose' | 'thread'
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [type, setType] = useState('FEEDBACK');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [reply, setReply] = useState('');

  const selected = threads.find((t) => t.id === selectedId) || null;

  const load = useCallback(async (keepView) => {
    setLoading(true);
    setError(null);
    try {
      const data = await feedbackService.getMyThreads(authenticatedFetch);
      setThreads(data);
      if (!keepView) setView(data.length === 0 ? 'compose' : 'list');
    } catch (err) {
      setError(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => { if (open) load(false); }, [open, load]);

  const captureContext = () => ({
    path: window.location.pathname,
    url: window.location.href,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userId: user?.id || null,
    demo: isDemoMode,
    organizationId: selectedOrganization?.id || null,
    organizationName: selectedOrganization?.name || null,
    environmentId: selectedEnvironment?.id || null,
    environmentName: selectedEnvironment?.name || null,
    whatsappConnected: !!isConnected,
    // persisted React/app state (selected org/env, builder drafts, prefs), redacted
    state: {
      localStorage: snapshotStorage(window.localStorage),
      sessionStorage: snapshotStorage(window.sessionStorage),
    },
    // live page state contributed by the current page (e.g. the flow being edited)
    page: safeDiagnostics(),
    at: new Date().toISOString(),
  });

  const handleClose = () => {
    setView('list'); setType('FEEDBACK'); setMessage(''); setEmail(''); setReply('');
    setSelectedId(null); setError(null); setBusy(false);
    onClose();
  };

  const submitNew = async () => {
    if (!message.trim()) { setError('Please describe your feedback or the issue.'); return; }
    if (isDemoMode && !isValidEmail(email.trim())) { setError('Please enter a valid email so we can follow up.'); return; }
    setBusy(true); setError(null);
    try {
      await feedbackService.submitFeedback({
        type, message: message.trim(), email: isDemoMode ? email.trim() : undefined, context: captureContext(),
      }, authenticatedFetch);
      setMessage(''); setType('FEEDBACK');
      await load(false);
    } catch (err) {
      setError(err.message || 'Failed to send feedback');
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setBusy(true); setError(null);
    try {
      await feedbackService.replyToThread(selected.id, reply.trim(), authenticatedFetch);
      setReply('');
      await load(true); // stay in the thread view
    } catch (err) {
      setError(err.message || 'Failed to send reply');
    } finally {
      setBusy(false);
    }
  };

  const title = view === 'compose' ? 'New feedback' : view === 'thread' ? 'Conversation' : 'Your feedback';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {view !== 'list' && (
          <IconButton size="small" onClick={() => { setError(null); setView('list'); }} aria-label="back">
            <BackIcon fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1 }}>{title}</Box>
        {view === 'list' && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => { setError(null); setView('compose'); }}>
            New
          </Button>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : view === 'compose' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <ToggleButtonGroup size="small" exclusive value={type} onChange={(e, v) => v && setType(v)}>
              <ToggleButton value="FEEDBACK">Feedback</ToggleButton>
              <ToggleButton value="BUG">Report a problem</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              autoFocus multiline minRows={4} fullWidth disabled={busy}
              label={type === 'BUG' ? 'What went wrong?' : 'Your feedback'}
              value={message} onChange={(e) => setMessage(e.target.value)}
            />
            {isDemoMode && (
              <TextField label="Your email" type="email" fullWidth disabled={busy}
                placeholder="so we can get back to you"
                value={email} onChange={(e) => setEmail(e.target.value)} />
            )}
            <Typography variant="caption" color="text.secondary">
              We include the page you're on{!isDemoMode && user?.email ? ` and your email (${user.email})` : ''} to help us debug.
            </Typography>
          </Box>
        ) : view === 'thread' && selected ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip size="small" variant="outlined" color={selected.type === 'BUG' ? 'error' : 'default'}
                label={selected.type === 'BUG' ? 'Problem' : 'Feedback'} />
              <Chip size="small" color={selected.status === 'RESOLVED' ? 'success' : 'warning'}
                label={selected.status === 'RESOLVED' ? 'Resolved' : 'Open'} />
            </Box>
            <FeedbackMessages messages={selected.messages} mineSender="USER" otherLabel="Team" />
          </Box>
        ) : threads.length === 0 ? (
          <Alert severity="info">No feedback yet. Use “New” to start one.</Alert>
        ) : (
          <List disablePadding>
            {threads.map((t) => (
              <ListItemButton key={t.id} divider
                onClick={() => { setSelectedId(t.id); setView('thread'); }}>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip size="small" variant="outlined" color={t.type === 'BUG' ? 'error' : 'default'}
                      label={t.type === 'BUG' ? 'Problem' : 'Feedback'} />
                    <Chip size="small" color={t.status === 'RESOLVED' ? 'success' : 'warning'}
                      label={t.status === 'RESOLVED' ? 'Resolved' : 'Open'} />
                  </Box>
                  <Typography variant="body2" noWrap>{lastBody(t)}</Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        {view === 'compose' && (
          <>
            <Button onClick={handleClose} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={submitNew} disabled={busy || !message.trim()}>
              {busy ? 'Sending…' : 'Send'}
            </Button>
          </>
        )}
        {view === 'thread' && selected && (
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <TextField size="small" fullWidth placeholder="Write a reply…" value={reply}
              disabled={busy} onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }} />
            <Button variant="contained" onClick={sendReply} disabled={busy || !reply.trim()}>Send</Button>
          </Box>
        )}
        {view === 'list' && <Button onClick={handleClose}>Close</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
